/**
 * Platform promo codes & gift cards: every call of
 * docs/payment-module/platform-offers-api-contract.md (§4 super admin, §5 public & tenant).
 *
 * Paths are relative to the api baseURL ("/api"), like every other caller of `api`. Ids
 * travel through `encodeURIComponent`. The wire is snake_case, money is integer cents,
 * and bodies are sent exactly as the contract names them: the write models are
 * extra="forbid" on the server, so nothing is added here that the caller did not pass.
 *
 * SHAPE-GUARDED. The production proxy answers 200 + index.html for an unknown `/api`
 * path, so a 200 whose body is not the shape the contract promises is refused with
 * `payloadShapeError` instead of being rendered as "no promos" or as an empty result. An
 * HTTP error is re-thrown untouched, so callers can read its `detail.code`
 * (./errors.js).
 *
 * There is no runtime switch to fixtures: ./fixtures.js is for tests only, which stub
 * "@/lib/api" itself.
 *
 * "@/lib/api", not a relative path: axios cannot load under Jest, and every suite that
 * mounts one of these surfaces stubs this exact specifier.
 */
import { api } from "@/lib/api";
// Relative: a pure module, resolvable under Jest without a shim.
import { payloadShapeError } from "../hooks/useAdminLoad";

const enc = encodeURIComponent;

// ── shape guards ─────────────────────────────────────────────────────────────

const isObject = (v) => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const isStr = (v) => typeof v === "string";
const isInt = (v) => Number.isInteger(v);
const isArrayOf = (v, pred) => Array.isArray(v) && v.every(pred);

const isPromo = (v) => isObject(v) && isStr(v.promo_id) && isStr(v.code)
  && isStr(v.discount_type) && isStr(v.duration) && isInt(v.used_count)
  && typeof v.active === "boolean";
const isGiftCard = (v) => isObject(v) && isStr(v.gift_card_id) && isStr(v.code)
  && isInt(v.amount_cents) && isInt(v.balance_cents) && isStr(v.status)
  && isObject(v.delivery);
const isRedemption = (v) => isObject(v) && isStr(v.redemption_id) && isStr(v.kind)
  && isStr(v.status) && isStr(v.surface);
const isPreview = (v) => isObject(v) && isStr(v.code)
  && ((v.kind === "promo" && isStr(v.discount_type) && isStr(v.duration)
       && isInt(v.plan_amount_cents) && isInt(v.discounted_amount_cents))
      || (v.kind === "giftcard" && isInt(v.credit_cents) && isStr(v.currency)));

const isPage = (pred) => (v) => isObject(v) && isInt(v.total) && isArrayOf(v.items, pred);
const isItems = (pred) => (v) => isObject(v) && isArrayOf(v.items, pred);

/** Returns `data` when `ok(data)`, otherwise throws the shape refusal. */
const guard = (data, ok) => {
  if (!ok(data)) throw payloadShapeError();
  return data;
};

// ── super admin, promos (§4.1) ───────────────────────────────────────────────

const HQ = "/super-admin/hq";

/** `GET /promos?limit&skip` -> {total, items: PromoOut[]} (newest first). */
export async function listPromos({ limit = 50, skip = 0 } = {}) {
  const { data } = await api.get(`${HQ}/promos`, { params: { limit, skip } });
  return guard(data, isPage(isPromo));
}

/** `POST /promos` PromoCreate -> PromoOut (201). */
export async function createPromo(body) {
  const { data } = await api.post(`${HQ}/promos`, body);
  return guard(data, isPromo);
}

/** `PATCH /promos/{id}` with any of {active, max_uses, expires_at, description, plan_ids} -> PromoOut. */
export async function updatePromo(promoId, patch) {
  const { data } = await api.patch(`${HQ}/promos/${enc(promoId)}`, patch);
  return guard(data, isPromo);
}

/** `GET /promos/{id}/redemptions` -> {items: (RedemptionOut + tenant_name)[]}. */
export async function promoRedemptions(promoId) {
  const { data } = await api.get(`${HQ}/promos/${enc(promoId)}/redemptions`);
  return guard(data, isItems(isRedemption));
}

// ── super admin, gift cards (§4.2) ───────────────────────────────────────────

/** `GET /giftcards?limit&skip` -> {total, items: GiftCardOut[]}. */
export async function listGiftCards({ limit = 50, skip = 0 } = {}) {
  const { data } = await api.get(`${HQ}/giftcards`, { params: { limit, skip } });
  return guard(data, isPage(isGiftCard));
}

/** `POST /giftcards` -> GiftCardOut (201), after the delivery email was attempted. */
export async function createGiftCard(body) {
  const { data } = await api.post(`${HQ}/giftcards`, body);
  return guard(data, isGiftCard);
}

/** `POST /giftcards/{id}/resend` -> GiftCardOut. */
export async function resendGiftCard(giftCardId) {
  const { data } = await api.post(`${HQ}/giftcards/${enc(giftCardId)}/resend`);
  return guard(data, isGiftCard);
}

/** `POST /giftcards/{id}/void` {reason} -> GiftCardOut (status "voided"). */
export async function voidGiftCard(giftCardId, reason) {
  const { data } = await api.post(`${HQ}/giftcards/${enc(giftCardId)}/void`, { reason });
  return guard(data, isGiftCard);
}

/** `GET /giftcards/{id}/redemptions` -> {items: (RedemptionOut + tenant_name)[]}. */
export async function giftCardRedemptions(giftCardId) {
  const { data } = await api.get(`${HQ}/giftcards/${enc(giftCardId)}/redemptions`);
  return guard(data, isItems(isRedemption));
}

// ── public & tenant (§5) ─────────────────────────────────────────────────────

/**
 * `POST /saas/codes/preview` {code, plan_id, currency, billing_cycle}
 *   -> promo preview | gift card preview (§5.1). Never mutates anything.
 */
export async function previewCode({ code, plan_id, currency, billing_cycle }) {
  const { data } = await api.post("/saas/codes/preview", { code, plan_id, currency, billing_cycle });
  return guard(data, isPreview);
}

/** `POST /tenant/billing/redeem` {code} -> RedemptionOut (status "applied" | "scheduled"). */
export async function redeemCode(code) {
  const { data } = await api.post("/tenant/billing/redeem", { code });
  return guard(data, isRedemption);
}

/** `GET /tenant/billing/redemptions` -> {items: RedemptionOut[]} for the current practice. */
export async function listTenantRedemptions() {
  const { data } = await api.get("/tenant/billing/redemptions");
  return guard(data, isItems(isRedemption));
}
